import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Form,
    DatePicker,
    Switch,
    Input,
    InputNumber,
    Select,
    Button,
    message,
    Tooltip,
    AutoComplete,
} from 'antd';
import { CalendarOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import s from './s.module.less';
import { EInvoiceChargeType, TSaveOrUpdateInvoice, TInvoiceLineItem, EInvoiceRelatedType } from 'types/invoice';
import useGetInvoiceOptions from 'hooks/useGetInvoiceOptions';
import { saveOrUpdateInvoice } from 'api/provider';
import { isNil } from 'lodash';
import useInvoiceDetail from 'hooks/useInvoiceDetail';

const { TextArea } = Input;

interface LineItem {
    id: string;
    chargeType?: EInvoiceChargeType;
    description?: string;
    quantity: number;
    amount?: number;
    relatedId?: number;
    relatedType?: EInvoiceRelatedType;
}

interface OtherOption {
    id: string;
    label: string;
}

interface InvoiceModalProps {
    open: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    invoiceNo?: string;
    id?: number;
    patientName?: string;
    state?: string;
    clientId?: number;
    textVisitId?: string; //默认选中某个item？
    /** Source text visit id for recurrence per-visit payment path (no-card flow) */
    sourceTextVisitId?: number;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({
    open,
    onCancel,
    onSuccess,
    invoiceNo,
    id,
    patientName = 'patient',
    state,
    clientId,
    sourceTextVisitId,
}) => {
    const [form] = Form.useForm();
    const [lineItems, setLineItems] = useState<LineItem[]>([
        {
            id: '1',
            chargeType: undefined,
            description: undefined,
            quantity: 1,
            amount: undefined,
            relatedId: undefined,
            relatedType: undefined,
        },
    ]);
    const [saving, setSaving] = useState(false);
    const [draftSaving, setDraftSaving] = useState(false);
    const [noteToPatientEnabled, setNoteToPatientEnabled] = useState(true);
    const {
        visitOptions,
        serviceOptions,
        otherOptions: apiOtherOptions,
        loading: optionsLoading,
    } = useGetInvoiceOptions(state || '', clientId);
    const [customOtherOptions, setCustomOtherOptions] = useState<OtherOption[]>([]);
    const [invoiceDetail, loadingInvoiceDetail] = useInvoiceDetail(invoiceNo || '');
    const isEditMode = !!invoiceNo;

    // Merge API otherOptions with custom user-added options
    const allOtherOptions = useMemo(() => {
        const apiOptions = (apiOtherOptions || []).map((opt) => ({
            id: opt.id,
            label: opt.description,
        }));
        // Combine API options with custom options, avoiding duplicates
        const combined = [...apiOptions];
        customOtherOptions.forEach((customOpt) => {
            if (!combined.find((opt) => opt.label === customOpt.label)) {
                combined.push(customOpt);
            }
        });
        return combined;
    }, [apiOtherOptions, customOtherOptions]);

    const ChargeTypes = [
        {
            label: 'Visits',
            value: EInvoiceChargeType.VISIT,
            desc: 'Bill for a scheduled visit with this patient (past or upcoming)',
            disabled: visitOptions.length === 0,
            disableTip: 'No unpaid or scheduled visits that can be billed',
        },
        {
            label: 'Services',
            value: EInvoiceChargeType.SERVICE,
            desc: "Bill for a service you offer in the patient's state",
            disabled: serviceOptions.length === 0,
            disableTip: "No services are available for this patient's state",
        },
        { label: 'Other', value: EInvoiceChargeType.OTHER, desc: 'Add a custom charge or description' },
    ];

    // 检查是否 visits 和 Services 都是 disabled，如果是则默认使用 other
    useEffect(() => {
        if (!open) return;
        const visitsDisabled = ChargeTypes.find((t) => t.value === EInvoiceChargeType.VISIT)?.disabled;
        const servicesDisabled = ChargeTypes.find((t) => t.value === EInvoiceChargeType.SERVICE)?.disabled;

        if (visitsDisabled && servicesDisabled && lineItems.length > 0 && !lineItems[0].chargeType) {
            setLineItems((prev) => {
                const updated = [...prev];
                updated[0] = { ...updated[0], chargeType: EInvoiceChargeType.OTHER };
                return updated;
            });
        }
    }, [open]);

    // 获取发票数据（编辑模式）
    useEffect(() => {
        if (isEditMode && open && invoiceDetail) {
            form.setFieldsValue({
                issueDate: invoiceDetail.invoice.issueDate ? dayjs(invoiceDetail.invoice.issueDate) : dayjs(),
                dueDate: invoiceDetail.invoice.dueDate ? dayjs(invoiceDetail.invoice.dueDate) : dayjs().add(7, 'day'),
                noteToPatient: invoiceDetail.invoice.noteToPatient || '',
            });
            setLineItems(
                invoiceDetail.lineItems.map((item) => ({
                    id: item.id.toString(),
                    chargeType: item.chargeType as EInvoiceChargeType,
                    description: item.description || '',
                    quantity: item.quantity || 0,
                    amount: item.unitPrice || 0,
                    relatedId: item.relatedId,
                    relatedType: item.relatedType,
                })),
            );
        } else if (!isEditMode && open) {
            form.setFieldsValue({
                issueDate: dayjs(), // 默认今天
                dueDate: dayjs().add(7, 'day'),
                noteToPatient: '',
            });
            setNoteToPatientEnabled(true);
            // 检查是否 visits 和 Services 都是 disabled，如果是则默认使用 other
            const visitsDisabled = ChargeTypes.find((t) => t.value === EInvoiceChargeType.VISIT)?.disabled;
            const servicesDisabled = ChargeTypes.find((t) => t.value === EInvoiceChargeType.SERVICE)?.disabled;
            const defaultChargeType = visitsDisabled && servicesDisabled ? EInvoiceChargeType.OTHER : undefined;
            setLineItems([
                { id: '1', chargeType: defaultChargeType, description: undefined, quantity: 1, amount: undefined },
            ]);
        }
    }, [isEditMode, open, invoiceNo, form, invoiceDetail]);

    // 计算每个 line item 的 total
    const calculateLineItemTotal = (quantity: number, amount: number) => {
        return quantity * amount;
    };

    // 计算总金额
    const totalAmount = useMemo(() => {
        return lineItems.reduce((sum, item) => {
            return sum + calculateLineItemTotal(item.quantity, item.amount || 0);
        }, 0);
    }, [lineItems]);

    // 添加新的 line item
    const handleAddLineItem = () => {
        const newItem: LineItem = {
            id: Date.now().toString(),
            chargeType: undefined,
            quantity: 1,
            amount: undefined,
        };
        setLineItems([...lineItems, newItem]);
    };

    // 删除 line item
    const handleDeleteLineItem = (itemId: string) => {
        if (lineItems.length > 1) {
            setLineItems(lineItems.filter((item) => item.id !== itemId));
        } else {
            message.warning('At least one line item is required.');
        }
    };

    // 更新 line item
    const handleLineItemChange = (itemId: string, field: keyof LineItem, value: any) => {
        setLineItems(
            lineItems.map((item) => {
                if (item.id === itemId) {
                    const updated = { ...item, [field]: value };
                    // 当 chargeType 改变时，清空 description
                    if (field === 'chargeType') {
                        updated.description = undefined;
                        updated.amount = undefined;
                    }
                    // 当选择 visit 或 service 时，自动设置 amount
                    if (
                        field === 'description' &&
                        (item.chargeType === EInvoiceChargeType.VISIT || item.chargeType === EInvoiceChargeType.SERVICE)
                    ) {
                        if (item.chargeType === EInvoiceChargeType.VISIT) {
                            const visit = visitOptions.find((v) => v.id === value);
                            if (visit) {
                                updated.amount = visit.price;
                            }
                        } else if (item.chargeType === EInvoiceChargeType.SERVICE) {
                            const service = serviceOptions.find((s) => s.id === value);
                            if (service) {
                                updated.amount = service.price;
                            }
                        }
                    }
                    return updated;
                }
                return item;
            }),
        );
    };

    // 处理 others 的自定义输入
    const handleOtherDescriptionChange = (itemId: string, value: string) => {
        handleLineItemChange(itemId, 'description', value);
    };

    // 处理发送发票
    const handleSendInvoice = async (isDraft: boolean) => {
        try {
            if (isDraft) {
                setDraftSaving(true);
            } else {
                setSaving(true);
            }
            const values = await form.validateFields();
            // 验证 line items
            const validLineItems = lineItems.filter(
                (item) => item.chargeType && item.description && item.quantity > 0 && item.amount && item.amount > 0,
            );
            if (validLineItems.length === 0) {
                message.error('Please add at least one valid line item.');
                return;
            }

            const formattedLineItems: TInvoiceLineItem[] = validLineItems.map((item) => {
                const params = {
                    description: item.description || '',
                    relatedId: item.relatedId,
                    relatedType: item.relatedType,
                };
                if (item.chargeType === EInvoiceChargeType.VISIT) {
                    const visit = visitOptions.find((v) => v.id === item.description);
                    if (visit) {
                        params.description = visit.time ? `${visit.description} - ${visit.time}` : visit.description;
                        params.relatedId = parseInt(visit.id);
                        params.relatedType = visit.isTextVisit ? EInvoiceRelatedType.TEXT_VISIT : EInvoiceRelatedType.APPOINTMENT;
                    }
                } else if (item.chargeType === EInvoiceChargeType.SERVICE) {
                    const service = serviceOptions.find((s) => s.id === item.description);
                    if (service) {
                        params.description = service.description;
                        params.relatedId = parseInt(service.id);
                        params.relatedType = EInvoiceRelatedType.SERVICE;
                    }
                } else if (item.chargeType === EInvoiceChargeType.OTHER) {
                    params.description = item.description || '';
                    params.relatedId = 0;
                    params.relatedType = EInvoiceRelatedType.OTHER;
                }
                return {
                    chargeType: item.chargeType!,
                    quantity: item.quantity,
                    unitPrice: item.amount || 0,
                    amount: item.quantity * item.amount!,
                    ...params,
                };
            });

            const noteToPatient = noteToPatientEnabled ? values.noteToPatient || '' : '';

            const params: TSaveOrUpdateInvoice = {
                ...(isEditMode && id ? { id } : {}),
                clientId: clientId!,
                issueDate: values.issueDate,
                dueDate: values.dueDate,
                noteToPatient,
                lineItems: formattedLineItems,
                sendInvoice: !isDraft,
                ...(sourceTextVisitId ? { sourceTextVisitId } : {}),
            };

            const res = await saveOrUpdateInvoice(params);
            if (!res.error) {
                message.success(isDraft ? 'Draft saved successfully.' : 'Invoice sent successfully.');
                onSuccess();
            }
        } catch (error) {
            console.error('Failed to send invoice:', error);
        } finally {
            if (isDraft) {
                setDraftSaving(false);
            } else {
                setSaving(false);
            }
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        setLineItems([{ id: '1', chargeType: undefined, quantity: 1, amount: undefined }]);
        setNoteToPatientEnabled(true);
        setCustomOtherOptions([]); // Reset custom options when modal closes
        onCancel();
    };

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            title={
                <div className={s.modalTitle}>
                    {isEditMode ? `Invoice to ${patientName}` : `New invoice to ${patientName}`}
                </div>
            }
            width={560}
            footer={null}
            className={s.invoiceModal}
            destroyOnClose
        >
            <div className={s.modalContent}>
                <div className={s.scrollableContent}>
                    <Form form={form} layout="vertical" className={s.invoiceForm}>
                        {/* Invoice Details Section */}
                        <div className={s.section}>
                            <Form.Item
                                label="Issue date"
                                name="issueDate"
                                className={s.formItem}
                                rules={[{ required: true, message: 'Please select issue date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="MMM DD, YYYY"
                                    suffixIcon={<CalendarOutlined />}
                                    placeholder="Select date"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Due date"
                                name="dueDate"
                                className={s.formItem}
                                rules={[{ required: true, message: 'Please select due date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="MMM DD, YYYY"
                                    suffixIcon={<CalendarOutlined />}
                                    placeholder="Select date"
                                />
                            </Form.Item>
                            <div className={s.noteSection}>
                                <div className={s.noteHeader}>
                                    <span className={s.noteTitle}>Note to patient</span>
                                    <Switch
                                        checked={noteToPatientEnabled}
                                        onChange={setNoteToPatientEnabled}
                                        className={s.noteSwitch}
                                    />
                                </div>
                                <Form.Item name="noteToPatient" className={s.noteTextArea} style={{ marginBottom: 0 }}>
                                    <TextArea
                                        placeholder="Optional note to explain what this invoice is for"
                                        rows={4}
                                        maxLength={250}
                                        showCount
                                        disabled={!noteToPatientEnabled}
                                        className={s.textArea}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Line Items Section */}
                        <div className={s.section}>
                            {lineItems.map((item, index) => (
                                <div key={item.id} className={s.lineItem}>
                                    <div className={s.lineItemHeader}>
                                        <span className={s.lineItemTitle}>Line item {index + 1}</span>
                                        {lineItems.length > 1 && (
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleDeleteLineItem(item.id)}
                                                className={s.deleteBtn}
                                            />
                                        )}
                                    </div>
                                    <div className={s.lineItemContent}>
                                        <Form.Item
                                            label="Charge type"
                                            className={s.formItem}
                                            style={{ marginBottom: 16 }}
                                        >
                                            <Select
                                                placeholder="Select a charge type"
                                                value={item.chargeType}
                                                onChange={(value) => handleLineItemChange(item.id, 'chargeType', value)}
                                                allowClear={false}
                                                optionLabelProp="label"
                                            >
                                                {ChargeTypes.map((type) => {
                                                    const optionContent = (
                                                        <div className={s.chargeTypeOption}>
                                                            <div className={s.chargeTypeHeader}>
                                                                <span
                                                                    className={
                                                                        type.disabled
                                                                            ? s.chargeTypeLabelDisabled
                                                                            : s.chargeTypeLabel
                                                                    }
                                                                >
                                                                    {type.label}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className={
                                                                    type.disabled
                                                                        ? s.chargeTypeDescDisabled
                                                                        : s.chargeTypeDesc
                                                                }
                                                            >
                                                                {type.desc}
                                                            </div>
                                                        </div>
                                                    );

                                                    return (
                                                        <Select.Option
                                                            key={type.value}
                                                            value={type.value}
                                                            disabled={type.disabled}
                                                            label={type.label}
                                                        >
                                                            {type.disabled && type.disableTip ? (
                                                                <Tooltip title={type.disableTip} placement="topRight">
                                                                    {optionContent}
                                                                </Tooltip>
                                                            ) : (
                                                                optionContent
                                                            )}
                                                        </Select.Option>
                                                    );
                                                })}
                                            </Select>
                                        </Form.Item>
                                        {item.chargeType && (
                                            <Form.Item
                                                label="Description"
                                                className={s.formItem}
                                                style={{ marginBottom: 16 }}
                                            >
                                                {item.chargeType === EInvoiceChargeType.VISIT && (
                                                    <Select
                                                        placeholder="Select a visit"
                                                        value={item.description}
                                                        onChange={(value) =>
                                                            handleLineItemChange(item.id, 'description', value)
                                                        }
                                                        showSearch
                                                        filterOption={(input, option) =>
                                                            String(option?.label ?? '')
                                                                .toLowerCase()
                                                                .includes(input.toLowerCase())
                                                        }
                                                        optionLabelProp="label"
                                                    >
                                                        {visitOptions.map((visit) => (
                                                            <Select.Option
                                                                key={visit.id}
                                                                value={visit.id}
                                                                label={visit.description}
                                                            >
                                                                <div className={s.visitOption}>
                                                                    <div className={s.visitOptionContent}>
                                                                        <div className={s.visitDescription}>
                                                                            {visit.description}
                                                                        </div>
                                                                        <div className={s.visitTime}>{visit.time}</div>
                                                                    </div>
                                                                    {isNil(visit.price) ? null : (
                                                                        <div className={s.visitPrice}>
                                                                            ${visit.price?.toFixed(2)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                )}
                                                {item.chargeType === EInvoiceChargeType.SERVICE && (
                                                    <Select
                                                        placeholder="Select a service"
                                                        value={item.description}
                                                        onChange={(value) =>
                                                            handleLineItemChange(item.id, 'description', value)
                                                        }
                                                        showSearch
                                                        filterOption={(input, option) =>
                                                            String(option?.label ?? '')
                                                                .toLowerCase()
                                                                .includes(input.toLowerCase())
                                                        }
                                                        optionLabelProp="label"
                                                    >
                                                        {serviceOptions.map((service) => (
                                                            <Select.Option
                                                                key={service.id}
                                                                value={service.id}
                                                                label={service.description}
                                                            >
                                                                <div className={s.serviceOption}>
                                                                    <div className={s.serviceDescription}>
                                                                        {service.description}
                                                                    </div>
                                                                    <div className={s.servicePrice}>
                                                                        ${service.price.toFixed(2)}
                                                                    </div>
                                                                </div>
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                )}
                                                {item.chargeType === EInvoiceChargeType.OTHER && (
                                                    <AutoComplete
                                                        value={item.description}
                                                        onChange={(value) => {
                                                            handleOtherDescriptionChange(item.id, value);
                                                            // 如果输入的值不在选项中，添加到自定义选项列表
                                                            if (
                                                                value &&
                                                                !allOtherOptions.find((o) => o.label === value)
                                                            ) {
                                                                const newOption: OtherOption = {
                                                                    id: Date.now().toString(),
                                                                    label: value,
                                                                };
                                                                setCustomOtherOptions((prev) => [...prev, newOption]);
                                                            }
                                                        }}
                                                        onSelect={(value) =>
                                                            handleOtherDescriptionChange(item.id, value)
                                                        }
                                                        onBlur={(e) => {
                                                            const { value } = e.target as HTMLInputElement;
                                                            // 当失去焦点时，如果输入的值不在选项中，添加到自定义选项列表
                                                            if (
                                                                value &&
                                                                !allOtherOptions.find((o) => o.label === value)
                                                            ) {
                                                                const newOption: OtherOption = {
                                                                    id: Date.now().toString(),
                                                                    label: value,
                                                                };
                                                                setCustomOtherOptions((prev) => [...prev, newOption]);
                                                            }
                                                        }}
                                                        options={allOtherOptions.map((opt) => ({
                                                            value: opt.label,
                                                            label: opt.label,
                                                        }))}
                                                        placeholder="Add a custom charge or description"
                                                        allowClear
                                                        filterOption={(inputValue, option) =>
                                                            String(option?.label ?? '')
                                                                .toLowerCase()
                                                                .includes(inputValue.toLowerCase())
                                                        }
                                                    />
                                                )}
                                            </Form.Item>
                                        )}
                                        <div className={s.lineItemRow}>
                                            <Form.Item
                                                label="Quantity"
                                                className={s.formItem}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    precision={0}
                                                    value={item.quantity}
                                                    onChange={(value) =>
                                                        handleLineItemChange(item.id, 'quantity', value || 1)
                                                    }
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label="Amount"
                                                className={s.formItem}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    precision={2}
                                                    value={item.amount}
                                                    onChange={(value) =>
                                                        handleLineItemChange(item.id, 'amount', value || 0)
                                                    }
                                                    prefix="$"
                                                    style={{ width: '100%' }}
                                                    controls={false}
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className={s.lineItemTotal}>
                                            <span className={s.totalLabel}>Total:</span>
                                            <span className={s.totalValue}>
                                                $
                                                {calculateLineItemTotal(item.quantity, item.amount || 0).toFixed(2) ||
                                                    '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <Button type="link" onClick={handleAddLineItem} className={s.addLineItemBtn}>
                                Add new line item
                            </Button>
                        </div>
                    </Form>
                </div>

                {/* Footer */}
                <div className={s.footer}>
                    <div className={s.footerTotal}>
                        <span className={s.totalLabel}>Total:</span>
                        <span className={s.totalValue}>${totalAmount.toFixed(2) || '-'}</span>
                    </div>
                    <div className={s.footerActions}>
                        {isEditMode ? (
                            <Button
                                type="link"
                                onClick={() => handleSendInvoice(true)}
                                className={s.saveDraftBtn}
                                loading={draftSaving}
                            >
                                Save and exit
                            </Button>
                        ) : (
                            <Button
                                type="link"
                                onClick={() => handleSendInvoice(true)}
                                className={s.saveDraftBtn}
                                loading={draftSaving}
                            >
                                Save as draft
                            </Button>
                        )}
                        <Button onClick={handleCancel} className={s.cancelBtn}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => handleSendInvoice(false)}
                            className={s.sendBtn}
                            loading={saving}
                        >
                            Send invoice
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default InvoiceModal;
